import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { usePageTitle } from '@/hooks/use-page-title';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/useToast';
import { usePlanningRunProgress } from '@/hooks/usePlanningRunProgress';
import {
  useEpicRequest,
  useReactToEpicRequest,
  useRemoveReactionFromEpicRequest,
  useEpicRequestComments,
  useCreateEpicRequestComment,
  useUpdateEpicRequestComment,
  useDeleteEpicRequestComment,
  useApproveEpicRequest,
  useRejectEpicRequest,
  useDeleteEpicRequest,
  useUpdateEpicRequest,
  useTransferEpicRequestScope,
  useEpicRequestInterview,
  epicRequestKeys,
} from '@/hooks/queries/use-epic-requests';
import { PlanningProgressPanel } from '@/components/epic-requests/planning-progress-panel';
import { ActiveJobCancel } from '@/components/mailbox/active-job-cancel';
import { LinkedInterviewLink } from '@/components/epic-requests/linked-interview-link';
import { RunOnMachineDialog } from '@/components/epic-requests/run-on-machine-dialog';
import { DiscussRequestSection } from '@/components/epic-requests/discuss-request';
import { improvePlanPathFromError } from '@/components/epics/plan-needs-review-banner';
import { useTeams } from '@/hooks/queries/use-teams';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MarkdownRenderer } from '@/components/common/markdown-renderer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSubItem,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, ThumbsUp, Flame, ThumbsDown, Edit, Trash2, Send, Check, X, Copy, ArrowRightLeft, User, Users, MoreVertical, RefreshCw, ExternalLink, Rocket, Loader2, Laptop } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { getInitials } from '@/lib/get-initials';
import { ComplianceDetailSection } from '@/components/ui/compliance-detail-section';
import { useAuditEpicRequest } from '@/hooks/queries/use-compliance';
import {
  buildPlannerFromRequestCommand,
  COPILOT_PROMPT_EXPLANATION,
} from '@/lib/chat/cli-guidance';
import { PullRequestReviewLink } from '@/components/pull-requests/pull-request-review-link';
import type { EpicRequestStatus, ReactionType, EpicRequestComment } from '@/lib/api/epic-requests';
import { DATABASE_CHOICE_LABELS } from '@/lib/api/epic-requests';

const COMMENT_MAX_LENGTH = 500;

// Status badge colors based on status
const statusColors: Record<EpicRequestStatus, string> = {
  pending: 'bg-yellow-900/30 text-yellow-300',
  approved: 'bg-green-900/30 text-green-300',
  rejected: 'bg-red-900/30 text-red-300',
  converted: 'bg-blue-900/30 text-blue-300',
  planning: 'bg-purple-900/30 text-purple-300',
};

// Status hue for ambient background glow
const statusHue: Record<EpicRequestStatus, string> = {
  pending: '#EAB308',
  approved: '#22C55E',
  rejected: '#EF4444',
  converted: '#3B82F6',
  planning: '#8B5CF6',
};

// Status text colors for menu items
const statusTextColors: Record<EpicRequestStatus, string> = {
  pending: 'text-yellow-300',
  approved: 'text-green-300',
  rejected: 'text-red-300',
  converted: 'text-blue-300',
  planning: 'text-purple-300',
};

// Valid status transitions (same as in status-transition-menu.tsx)
const statusTransitions: Partial<Record<EpicRequestStatus, EpicRequestStatus[]>> = {
  approved: ['pending', 'rejected', 'converted', 'planning'],
  planning: ['approved', 'converted'],
  converted: ['pending', 'approved', 'rejected'],
};

export function EpicRequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { toast } = useToast();
  // ENG-290: set when conversion is refused because the epic's plan is critical-fail.
  const [improvePlanPath, setImprovePlanPath] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const { data: request, isLoading } = useEpicRequest(requestId ?? '');
  // Live planning progress (ENG-369): SSE-driven, with fallback polling inside the hook.
  const { progress: planningProgress, error: planningProgressError } = usePlanningRunProgress(
    request?.status === 'planning' ? requestId : null,
  );
  usePageTitle(request?.title ?? "Request");
  const reactMutation = useReactToEpicRequest();
  const removeReactionMutation = useRemoveReactionFromEpicRequest();

  // Comments
  const {
    data: commentsData,
    isLoading: apiCommentsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useEpicRequestComments(requestId ?? '');

  const commentsLoading = apiCommentsLoading;

  const createCommentMutation = useCreateEpicRequestComment();
  const updateCommentMutation = useUpdateEpicRequestComment();
  const deleteCommentMutation = useDeleteEpicRequestComment();

  // Admin actions
  const approveMutation = useApproveEpicRequest();
  const rejectMutation = useRejectEpicRequest();
  const deleteRequestMutation = useDeleteEpicRequest();
  const updateRequestMutation = useUpdateEpicRequest();
  const transferMutation = useTransferEpicRequestScope();
  const auditEpicRequest = useAuditEpicRequest();

  // Comment form state
  const [newCommentContent, setNewCommentContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [deleteCommentDialogId, setDeleteCommentDialogId] = useState<string | null>(null);
  const [glowExiting, setGlowExiting] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  const { data: teamsData } = useTeams();
  const queryClient = useQueryClient();

  // Continue interview (ENG-148): fetch the linked interview so we can offer a
  // "Continue interview" action when it is completed AND this ER is still
  // pending. The linkage endpoint is owner-scoped, so gate the fetch on the
  // viewer being the creator (avoids a 403 for team members).
  const isRequestOwner = !!user && !!request && user.id === request.requestedById;
  const { data: linkedInterview } = useEpicRequestInterview(requestId ?? '', {
    enabled: isRequestOwner,
  });

  // Poll for planning state as a resilient fallback (SSE is the primary mechanism).
  // Polls during both 'approved' (may transition to 'planning' imminently) and 'planning'.
  useEffect(() => {
    if (request?.status !== 'planning' && request?.status !== 'approved') return;
    const interval = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.detail(requestId ?? ''), exact: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [request?.status, requestId, queryClient]);

  const handleBack = useCallback(() => {
    setGlowExiting(true);
    setTimeout(() => void navigate('/epic-requests'), 400);
  }, [navigate]);

  const handleCopyCommand = useCallback(async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = command;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);

      try {
        textArea.select();
        // eslint-disable-next-line deprecation/deprecation
        const successful = document.execCommand('copy');
        if (successful) {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        }
      } catch {
        // Swallow copy fallback errors to avoid unhandled promise rejections
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-6 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="pt-4">
        <p className="text-muted-foreground">Epic request not found</p>
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mt-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to epic requests
        </Button>
      </div>
    );
  }

  // Get reaction counts
  const getReactionCount = (type: ReactionType): number => {
    const reaction = request.reactionCounts?.find((r) => r.reactionType === type);
    return reaction?.count ?? 0;
  };

  const likeCount = getReactionCount('like');
  const fireCount = getReactionCount('fire');
  const dislikeCount = getReactionCount('dislike');

  // Check if user has reacted and what type
  const userReaction = request.userReaction as ReactionType | null;

  // Check if current user is the creator (will be used in next tasks)
  // const isCreator = user?.id === request.requestedById;

  // Handle reaction toggle
  const handleReaction = async (type: ReactionType) => {
    if (!requestId) return;

    // If clicking the same reaction, remove it
    if (userReaction === type) {
      await removeReactionMutation.mutateAsync(requestId);
    } else {
      // Otherwise, add/change reaction
      await reactMutation.mutateAsync({
        id: requestId,
        input: { reactionType: type },
      });
    }
  };

  // Flatten all comments from pages
  const apiComments = commentsData?.pages.flatMap((page) => page.data) ?? [];
  const allComments = apiComments;

  // Handle create comment
  const handleCreateComment = async () => {
    if (!requestId || !newCommentContent.trim()) return;

    await createCommentMutation.mutateAsync({
      id: requestId,
      input: { content: newCommentContent.trim() },
    });
    setNewCommentContent('');
  };

  // Handle edit comment
  const handleStartEdit = (comment: EpicRequestComment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!requestId || !editingContent.trim()) return;

    await updateCommentMutation.mutateAsync({
      epicRequestId: requestId,
      commentId,
      input: { content: editingContent.trim() },
    });
    setEditingCommentId(null);
    setEditingContent('');
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId: string) => {
    if (!requestId) return;
    await deleteCommentMutation.mutateAsync({
      epicRequestId: requestId,
      commentId,
    });
    setDeleteCommentDialogId(null);
  };

  // Admin action handlers
  const handleApprove = async () => {
    if (!requestId) return;
    await approveMutation.mutateAsync(requestId);
  };

  const handleReject = async () => {
    if (!requestId) return;
    await rejectMutation.mutateAsync(requestId);
  };

  const handleDeleteRequest = async () => {
    if (!requestId) return;
    if (
      window.confirm(
        'Are you sure you want to delete this epic request? This action cannot be undone.'
      )
    ) {
      await deleteRequestMutation.mutateAsync(requestId);
      void navigate('/epic-requests');
    }
  };

  const handleStatusChange = async (newStatus: EpicRequestStatus) => {
    if (!requestId) return;
    try {
      await updateRequestMutation.mutateAsync({ id: requestId, input: { status: newStatus } });
      setImprovePlanPath(null);
      toast(`Status changed to ${newStatus}`);
    } catch (error) {
      setImprovePlanPath(improvePlanPathFromError(error));
      const message = error instanceof Error ? error.message : 'Failed to update status';
      toast(message);
    }
  };

  const handleEdit = () => {
    // Navigate to edit page (not yet implemented, placeholder)
    void navigate(`/epic-requests/${requestId ?? ''}/edit`);
  };

  // Transfer handlers
  const handleMoveToTeam = () => {
    setSelectedTeamId('');
    setTransferDialogOpen(true);
  };

  const handleConfirmMoveToTeam = async () => {
    if (!requestId || !selectedTeamId) return;
    try {
      await transferMutation.mutateAsync({
        id: requestId,
        input: { direction: 'personal-to-team', teamId: selectedTeamId },
      });
      setTransferDialogOpen(false);
      setSelectedTeamId('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to transfer request';
      toast(message);
    }
  };

  const plannerCommand = buildPlannerFromRequestCommand(request.identifier ?? request.title);

  // Determine scope
  const isPersonalScope = !!request.personalScopeId;
  const teamLabel = request.team?.level === 'product' ? 'Product' : 'Team';

  // Determine permissions
  const isAdmin = user?.isGlobalAdmin ?? false;
  const isCreator = user?.id === request.requestedById;
  const canEdit = isCreator && request.status !== 'approved' && request.status !== 'converted';
  const canDelete = isAdmin;
  const canApproveReject = isAdmin || isCreator;

  const hueColor = statusHue[request.status];

  return (
    <div className="relative pt-4">
      {/* Background hue glow — even ambient tint + top glow */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${hueColor}2B, transparent 70%)`,
          backgroundColor: `${hueColor}09`,
          animation: `${glowExiting ? 'glow-exit' : 'glow-enter'} 0.5s ease-out forwards`,
        }}
      />

      <div className="relative z-10">
      {improvePlanPath && (
        <div
          role="alert"
          data-testid="conversion-blocked-alert"
          className="glass-card rounded-2xl mb-4 px-4 md:px-6 py-3 flex flex-col gap-2 sm:flex-row sm:items-center border border-red-800/40"
        >
          <p className="text-sm flex-1">
            <span className="font-semibold text-red-300">Can&apos;t convert yet.</span>{' '}
            <span className="text-muted-foreground">The linked epic&apos;s plan failed its audit.</span>
          </p>
          <Link to={improvePlanPath} className="text-sm font-medium text-red-300 underline underline-offset-2 hover:text-red-200">
            Improve plan
          </Link>
        </div>
      )}
      {/* Header */}
      <div className="glass-card rounded-2xl mx-0 mb-4">
        <div className="px-4 md:px-6 py-3 md:py-5">
          {/* Top row: title + actions */}
          <div className={cn("flex justify-between gap-3", isMobile ? "flex-col" : "items-center")}>
            <div className="flex-1 min-w-0">
              {/* Identifier + title with overflow menu on mobile */}
              <div className="flex items-start justify-between gap-2">
                <h1 className={cn("font-semibold leading-snug", isMobile ? "text-lg" : "text-xl truncate")}>
                  {request.identifier && (
                    <span className="text-xs text-muted-foreground font-mono mr-1.5">{request.identifier}</span>
                  )}
                  {request.title}
                </h1>
                {/* Overflow menu — top-right on mobile */}
                {isMobile && (() => {
                  const validTransitions = statusTransitions[request.status] ?? [];
                  const canTransitionStatus = (isCreator || isAdmin) && validTransitions.length > 0;
                  const showMenu = canDelete || isCreator || canTransitionStatus || canEdit || (canApproveReject && request.status === 'pending');
                  if (!showMenu) return null;
                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="shrink-0 -mt-1 -mr-1">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEdit && (
                          <DropdownMenuItem onClick={() => { handleEdit(); }}>
                            <Edit className="h-3.5 w-3.5 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {canApproveReject && request.status === 'pending' && (
                          <>
                            <DropdownMenuItem onClick={() => { void handleApprove(); }} className="text-green-400">
                              <Check className="h-3.5 w-3.5 mr-2" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { void handleReject(); }} className="text-red-400">
                              <X className="h-3.5 w-3.5 mr-2" />
                              Reject
                            </DropdownMenuItem>
                          </>
                        )}
                        {canTransitionStatus && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <RefreshCw className="h-3.5 w-3.5 mr-2" />
                                Change Status
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                {validTransitions.map((targetStatus) => (
                                  <DropdownMenuSubItem
                                    key={targetStatus}
                                    onClick={() => { void handleStatusChange(targetStatus); }}
                                    className={cn('capitalize cursor-pointer', statusTextColors[targetStatus])}
                                  >
                                    {targetStatus}
                                  </DropdownMenuSubItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          </>
                        )}
                        {isPersonalScope && isCreator && (
                          <DropdownMenuItem onClick={() => { handleMoveToTeam(); }}>
                            <ArrowRightLeft className="h-3.5 w-3.5 mr-2" />
                            Move to project
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => { void handleDeleteRequest(); }}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })()}
              </div>
              {/* Subtitle: avatar + name + time + badges */}
              <div className="flex flex-wrap items-center gap-1.5 md:gap-3 mt-2">
                <Avatar className="h-5 w-5 md:h-6 md:w-6 border border-white/[0.08]">
                  {request.requestedBy?.avatarUrl && <AvatarImage src={request.requestedBy.avatarUrl} alt={request.requestedBy?.name ?? 'Unknown'} />}
                  <AvatarFallback className="text-[10px] bg-white/[0.06] font-medium">
                    {getInitials(request.requestedBy?.name ?? '?')}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs md:text-sm text-muted-foreground">
                  {request.requestedBy?.name ?? 'Unknown'}
                </span>
                <span className="text-muted-foreground text-xs">•</span>
                <span className="text-xs md:text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                </span>
                <Badge
                  className={cn(
                    'text-[10px] md:text-xs font-medium px-1.5 py-0',
                    isPersonalScope
                      ? 'bg-purple-900/30 text-purple-300'
                      : 'bg-white/[0.06] text-muted-foreground'
                  )}
                >
                  {isPersonalScope ? (
                    <><User className="h-3 w-3 mr-0.5" />Personal</>
                  ) : (
                    <><Users className="h-3 w-3 mr-0.5" />{teamLabel}{request.team?.name ? `: ${request.team.name}` : ''}</>
                  )}
                </Badge>
                <Badge
                  className={cn('text-[10px] md:text-xs font-medium capitalize px-1.5 py-0', statusColors[request.status])}
                >
                  {request.status}
                </Badge>
              </div>
            </div>

            {/* Actions — desktop only (mobile uses overflow menu above) */}
            {!isMobile && (
            <div className="flex items-center gap-2 shrink-0">
              {/* Edit Button */}
              {canEdit && (
                <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => { handleEdit(); }}>
                  <Edit className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </Button>
              )}

              {/* Approve/Reject Buttons — primary admin actions */}
              {canApproveReject && request.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs text-green-400 hover:text-green-300 border-green-800/50"
                    onClick={() => { void handleApprove(); }}
                    disabled={approveMutation.isPending}
                  >
                    <Check className="h-3.5 w-3.5 mr-1.5" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs text-red-400 hover:text-red-300 border-red-800/50"
                    onClick={() => { void handleReject(); }}
                    disabled={rejectMutation.isPending}
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Reject
                  </Button>
                </>
              )}

              {/* Overflow menu — Delete, Transfer, Status Changes */}
              {(() => {
                const validTransitions = statusTransitions[request.status] ?? [];
                const canTransitionStatus = (isCreator || isAdmin) && validTransitions.length > 0;
                const showMenu = canDelete || isCreator || canTransitionStatus;
                
                if (!showMenu) return null;
                
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Status transition submenu — for creators and admins on approved/converted requests */}
                      {canTransitionStatus && (
                        <>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <RefreshCw className="h-3.5 w-3.5 mr-2" />
                              Change Status
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {validTransitions.map((targetStatus) => (
                                <DropdownMenuSubItem
                                  key={targetStatus}
                                  onClick={() => { void handleStatusChange(targetStatus); }}
                                  className={cn('capitalize cursor-pointer', statusTextColors[targetStatus])}
                                >
                                  {targetStatus}
                                </DropdownMenuSubItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          {(isCreator || canDelete) && <DropdownMenuSeparator />}
                        </>
                      )}
                      {/* Transfer options */}
                      {isPersonalScope && isCreator && (
                        <DropdownMenuItem onClick={() => { handleMoveToTeam(); }}>
                          <ArrowRightLeft className="h-3.5 w-3.5 mr-2" />
                          Move to project
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => { void handleDeleteRequest(); }}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
            </div>
            )}
          </div>
        </div>

        {/* Planner Command — attached to header, only for approved unconverted requests */}
        {/* Shown on all breakpoints (ENG-163 dispatch must be reachable on
            mobile, where there is no terminal at all). */}
        {request.status === 'approved' && !request.convertedEpicId && (
          <div className="border-t border-green-800/30 px-4 md:px-6 py-4 bg-green-900/[0.06]">
            <div className="flex items-center gap-2 mb-3">
              <Rocket className="h-4 w-4 text-green-400" />
              <span className="text-sm font-semibold text-green-300">Ready to Convert</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              This request has been approved. Run the command below in GitHub Copilot to create an epic from it.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-white/[0.03] border border-green-800/30 px-3 py-2 text-sm font-mono select-all overflow-x-auto">
                {plannerCommand}
              </code>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 border-green-800/30 hover:border-green-700/50"
                onClick={() => { void handleCopyCommand(plannerCommand); }}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {COPILOT_PROMPT_EXPLANATION}
            </p>
            {/* Send-to-my-machine (ENG-163): dispatch the planner to a paired
                machine — no terminal needed — as a start_execution/plan job. */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">or run it without a terminal:</span>
              <RunOnMachineDialog
                jobType="start_execution"
                payload={{ kind: 'plan', epicRequestId: requestId ?? '' }}
                targetRemote={request?.team?.gitRemote ?? null}
              >
                <Laptop className="h-4 w-4 mr-2" />
                Send to my machine
              </RunOnMachineDialog>
            </div>
          </div>
        )}
        {/* Converted: show link to the created epic */}
        {request.status === 'converted' && request.convertedEpicId && (
          <div className="border-t border-blue-800/30 px-4 md:px-6 py-4 bg-blue-900/[0.06]">
            <div className="flex items-center gap-2 mb-2">
              <Check className="h-4 w-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-300">Converted to Epic</span>
            </div>
            <Link
              to={`/epics/${request.convertedEpicId}`}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-800/30 bg-white/[0.02] px-3 py-2 text-sm hover:bg-white/[0.06] transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5 text-blue-400" />
              <span className="font-medium">
                {request.convertedEpic?.identifier && (
                  <span className="font-mono text-blue-400 mr-1.5">{request.convertedEpic.identifier}</span>
                )}
                {request.convertedEpic?.name ?? 'View Epic'}
              </span>
            </Link>
          </div>
        )}
        {/* The pull request built from this request (ENG-298) */}
        {requestId && request.status === 'converted' && (
          <div className="border-t border-white/[0.06] px-4 md:px-6 py-3 text-sm empty:hidden">
            <PullRequestReviewLink epicRequestId={requestId} />
          </div>
        )}
        {/* Planning in progress banner */}
        {request.status === 'planning' && (
          <div className="border-t border-purple-800/30 px-4 md:px-6 py-3 bg-purple-900/[0.06]">
            <div className="flex items-center gap-2 text-purple-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm font-medium">Planning in progress...</span>
            </div>
          </div>
        )}
        {/* Linked interview (ENG-147) — owner-scoped deep-link to the driving
            mailbox job. Gated on isCreator so the owner-scoped fetch isn't
            fired for team members viewing someone else's request. */}
        {requestId && isCreator && (
          <LinkedInterviewLink requestId={requestId} enabled={isCreator} />
        )}

        {/* Discuss this request (ENG-291): on every request, whatever its
            status. Resumes or continues the linked discussion, or starts one
            linked to this request; converted requests can send it to the
            planner. Supersedes the pending-only "Add more detail" (ENG-148). */}
        <DiscussRequestSection request={request} linkedInterview={linkedInterview} />

        {/* A plan running for this request can be cancelled from here (ENG-E56). */}
        {requestId && <ActiveJobCancel query={{ epicRequestId: requestId }} label="Planning" />}
      </div>

      {/* Planning Progress Panel */}
      {request.status === 'planning' && (
        <div className="glass-card rounded-2xl mx-0 mb-4">
          <div className="px-4 md:px-6 py-4">
            {planningProgress ? (
              <PlanningProgressPanel progress={planningProgress} convertedEpicId={request.convertedEpicId} />
            ) : planningProgressError ? (
              <div className="text-sm text-muted-foreground">
                Unable to load planning progress.
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Loading planning progress…</div>
            )}
          </div>
        </div>
      )}

      {/* Compliance Section */}
      <div className="glass-card rounded-2xl mx-0 mb-4">
        <div className="px-4 md:px-6 py-4">
          <ComplianceDetailSection
            score={request.complianceScore}
            onReaudit={() => { void auditEpicRequest.mutateAsync(request.id); }}
            isAuditing={auditEpicRequest.isPending}
          />
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
      <div className="glass-card rounded-2xl">
        <div className={cn("mx-auto space-y-6", isMobile ? "p-4" : "max-w-4xl p-6")}>

          {/* Description */}
          {request.description && (
            <div className="prose dark:prose-invert max-w-none">
              <MarkdownRenderer content={request.description} />
            </div>
          )}

          {/* Originating Ticket Link */}
          {request.originatingTicketUrl && (() => {
            // Defensively gate the rendered href on http(s) protocol so that
            // legacy/imported data or a future server-side validation regression
            // cannot turn this into a `javascript:` link.
            let safeHref: string | null = null;
            try {
              const parsed = new URL(request.originatingTicketUrl);
              if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
                safeHref = parsed.toString();
              }
            } catch {
              safeHref = null;
            }
            return (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-muted-foreground">Originating ticket:</span>
                {safeHref ? (
                  <a
                    href={safeHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                  >
                    {(() => {
                      try {
                        return new URL(safeHref).hostname;
                      } catch {
                        return 'Source ticket';
                      }
                    })()}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <span className="text-muted-foreground break-all">
                    {request.originatingTicketUrl}
                  </span>
                )}
              </div>
            );
          })()}

          {/* Structured Description */}
          {request.structuredDesc && (
            <div className="space-y-4 border-t pt-6">
              {request.structuredDesc.problemStatement && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Problem Statement</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.problemStatement}
                  </p>
                </div>
              )}

              {request.structuredDesc.proposedSolution && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Proposed Solution</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.proposedSolution}
                  </p>
                </div>
              )}

              {request.structuredDesc.impactAssessment && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Impact Assessment</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.impactAssessment}
                  </p>
                </div>
              )}

              {request.structuredDesc.targetAudience && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Target Audience</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.targetAudience}
                  </p>
                </div>
              )}

              {request.structuredDesc.successMetrics && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Success Metrics</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.successMetrics}
                  </p>
                </div>
              )}

              {request.structuredDesc.alternatives && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Alternatives</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.alternatives}
                  </p>
                </div>
              )}

              {request.structuredDesc.dependencies && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Dependencies</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.dependencies}
                  </p>
                </div>
              )}

              {request.structuredDesc.estimatedEffort && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Estimated Effort</h3>
                  <p className="text-sm text-muted-foreground">
                    {request.structuredDesc.estimatedEffort}
                  </p>
                </div>
              )}

              {(request.structuredDesc.databaseChoice !== undefined || Boolean(request.structuredDesc.databaseNote)) && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Database</h3>
                  {request.structuredDesc.databaseChoice && (
                    <p className="text-sm text-muted-foreground">
                      {DATABASE_CHOICE_LABELS[request.structuredDesc.databaseChoice]}
                    </p>
                  )}
                  {request.structuredDesc.databaseNote && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {request.structuredDesc.databaseNote}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reactions */}
          <div className="border-t pt-6">
            <h3 className="text-sm font-semibold mb-3">Reactions</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={userReaction === 'like' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { void handleReaction('like'); }}
                disabled={reactMutation.isPending || removeReactionMutation.isPending}
                className={cn(
                  'gap-2',
                  userReaction === 'like' && 'bg-blue-500 hover:bg-blue-600'
                )}
              >
                <ThumbsUp className="h-4 w-4" />
                <span>Like</span>
                {likeCount > 0 && (
                  <span className="ml-1 font-semibold">{likeCount}</span>
                )}
              </Button>

              {/* Fire Button */}
              <Button
                variant={userReaction === 'fire' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { void handleReaction('fire'); }}
                disabled={reactMutation.isPending || removeReactionMutation.isPending}
                className={cn(
                  'gap-2',
                  userReaction === 'fire' && 'bg-orange-500 hover:bg-orange-600'
                )}
              >
                <Flame className="h-4 w-4" />
                <span>Fire</span>
                {fireCount > 0 && (
                  <span className="ml-1 font-semibold">{fireCount}</span>
                )}
              </Button>

              {/* Dislike Button */}
              <Button
                variant={userReaction === 'dislike' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { void handleReaction('dislike'); }}
                disabled={reactMutation.isPending || removeReactionMutation.isPending}
                className={cn(
                  'gap-2',
                  userReaction === 'dislike' && 'bg-red-500 hover:bg-red-600'
                )}
              >
                <ThumbsDown className="h-4 w-4" />
                <span>Dislike</span>
                {dislikeCount > 0 && (
                  <span className="ml-1 font-semibold">{dislikeCount}</span>
                )}
              </Button>
            </div>
          </div>

          {/* Comments Section — separate card */}
        </div>
      </div>

      <div className="glass-card rounded-2xl">
        <div className={cn("mx-auto", isMobile ? "p-4" : "max-w-4xl p-6")}>
            <h3 className="text-sm font-semibold mb-4">
              Comments ({allComments.length})
            </h3>

            {/* Add Comment Form */}
            <div className="mb-6 space-y-2">
              <Textarea
                placeholder="Add a comment..."
                value={newCommentContent}
                onChange={(e) => {
                  if (e.target.value.length <= COMMENT_MAX_LENGTH) {
                    setNewCommentContent(e.target.value);
                  }
                }}
                maxLength={COMMENT_MAX_LENGTH}
                className="min-h-[80px]"
              />
              <div className="flex items-center justify-between">
                <span className={cn(
                  'text-xs',
                  newCommentContent.length >= COMMENT_MAX_LENGTH ? 'text-red-400' : 'text-muted-foreground'
                )}>
                  {newCommentContent.length}/{COMMENT_MAX_LENGTH}
                </span>
                <Button
                  onClick={() => { void handleCreateComment(); }}
                  disabled={
                    !newCommentContent.trim() || createCommentMutation.isPending
                  }
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Post Comment
                </Button>
              </div>
            </div>

            {/* Comments List */}
            {commentsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="animate-pulse space-y-2">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-16 w-full bg-muted rounded" />
                  </div>
                ))}
              </div>
            ) : allComments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              <div className="space-y-4">
                {allComments.map((comment) => {
                  const isAuthor = user?.id === comment.authorId;
                  const isAdmin = user?.isGlobalAdmin;
                  const isEditing = editingCommentId === comment.id;

                  return (
                    <div
                      key={comment.id}
                      className="border border-white/[0.06] rounded-lg p-4 space-y-2"
                    >
                      {/* Comment Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 text-sm">
                          <Avatar className="h-8 w-8 border-2 border-white/[0.08]">
                            {comment.author.avatarUrl && <AvatarImage src={comment.author.avatarUrl} alt={comment.author.name} />}
                            <AvatarFallback className="text-xs bg-white/[0.06] font-medium">
                              {getInitials(comment.author.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">
                            {comment.author.name}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground">
                            {formatDistanceToNow(new Date(comment.createdAt), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                        {(isAuthor || isAdmin) && !isEditing && (
                          <div className="flex items-center gap-1">
                            {isAuthor && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-11 w-11 md:h-7 md:w-7"
                                onClick={() => { handleStartEdit(comment); }}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {(isAuthor || isAdmin) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-11 w-11 md:h-7 md:w-7 text-destructive hover:text-destructive"
                                onClick={() => { setDeleteCommentDialogId(comment.id); }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Comment Content */}
                      {isEditing ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editingContent}
                            onChange={(e) => {
                              if (e.target.value.length <= COMMENT_MAX_LENGTH) {
                                setEditingContent(e.target.value);
                              }
                            }}
                            maxLength={COMMENT_MAX_LENGTH}
                            className="min-h-[80px]"
                          />
                          <div className="flex items-center justify-between">
                            <span className={cn(
                              'text-xs',
                              editingContent.length >= COMMENT_MAX_LENGTH ? 'text-red-400' : 'text-muted-foreground'
                            )}>
                              {editingContent.length}/{COMMENT_MAX_LENGTH}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => { void handleSaveEdit(comment.id); }}
                                disabled={
                                  !editingContent.trim() ||
                                  updateCommentMutation.isPending
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap pl-[42px]">
                          {comment.content}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Load More Comments */}
                {hasNextPage && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="outline"
                      onClick={() => { void fetchNextPage(); }}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load More Comments'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      </div>

      {/* Delete Comment Confirmation Dialog */}
      <Dialog open={!!deleteCommentDialogId} onOpenChange={(open) => { if (!open) setDeleteCommentDialogId(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete comment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setDeleteCommentDialogId(null); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => { if (deleteCommentDialogId) void handleDeleteComment(deleteCommentDialogId); }}
              disabled={deleteCommentMutation.isPending}
            >
              {deleteCommentMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer to Team Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={(open) => { if (!open) { setTransferDialogOpen(false); setSelectedTeamId(''); } }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Move to project</DialogTitle>
            <DialogDescription>
              Select a team to move this request to. It will become visible to all team members and require approval.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a team..." />
              </SelectTrigger>
              <SelectContent>
                {teamsData?.data.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}{team.key ? ` (${team.key})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setTransferDialogOpen(false); setSelectedTeamId(''); }}>
              Cancel
            </Button>
            <Button
              onClick={() => { void handleConfirmMoveToTeam(); }}
              disabled={!selectedTeamId || transferMutation.isPending}
            >
              {transferMutation.isPending ? 'Moving...' : 'Move to project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
